import * as React from 'react';

/**
 * Focus restoration for kit overlays opened without a `DialogTrigger`.
 *
 * Radix's modal `Dialog.Content` / `AlertDialog.Content` end their close sequence with
 * `event.preventDefault(); context.triggerRef.current?.focus()`. That only restores focus when
 * the overlay was opened by a `Dialog.Trigger`. Forge opens these overlays from toolbar buttons,
 * table row actions and keyboard shortcuts instead, so `triggerRef` is null and focus falls to
 * `<body>` — the user is dropped at the top of the page and a screen reader loses its place.
 *
 * The opener is captured by a marker rendered INSIDE the Radix content. Radix keeps the content
 * unmounted until the overlay opens, so the marker's first render is the opening commit; no
 * effect has run yet at that point, which means the focus scope has not moved focus and
 * `document.activeElement` is still the control the user activated. Capturing per opening
 * (rather than reading a module-level "last focused element" at close time) keeps the restore
 * target tied to this particular opening, and lets an overlay opened from inside another overlay
 * return focus to the control within its parent.
 */
function currentOpener(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const active = document.activeElement;
  // `document.body` is focusable-ish and always connected, but restoring to it is the very
  // defect this module exists to avoid; treat it as "nothing was focused".
  if (!(active instanceof HTMLElement) || active === document.body) return null;
  if (active.hasAttribute('data-radix-focus-guard')) return null;
  return active;
}

export interface InvokerFocusRestore {
  /**
   * Render this as a child of the Radix content element. It draws nothing; it exists so the
   * capture happens when Radix mounts the content rather than when the host component that
   * holds the (often permanently mounted) `Dialog` root first renders.
   */
  OpenerCapture: React.FC;
  /**
   * An `onCloseAutoFocus` handler that hands focus back to the control that opened the overlay.
   * Calling `preventDefault` stops Radix's own trigger-focusing handler, because Radix composes
   * the caller's handler ahead of its own and skips its own once the event is defaulted. When no
   * opener was recorded it does nothing, leaving Radix's behaviour untouched.
   */
  restoreFocus: (event: Event) => void;
}

export function useRestoreFocusToInvoker(): InvokerFocusRestore {
  const invokerRef = React.useRef<HTMLElement | null>(null);

  const OpenerCapture = React.useMemo<React.FC>(() => {
    const Capture: React.FC = () => {
      const captured = React.useRef(false);
      if (!captured.current) {
        captured.current = true;
        invokerRef.current = currentOpener();
      }
      return null;
    };
    Capture.displayName = 'OpenerCapture';
    return Capture;
  }, []);

  const restoreFocus = React.useCallback((event: Event) => {
    const invoker = invokerRef.current;
    if (!invoker || !invoker.isConnected) return;
    event.preventDefault();
    invoker.focus();
  }, []);

  return { OpenerCapture, restoreFocus };
}
